import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import assert from 'assert';
import type { LegacyRef } from 'react';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import TopNavBarComponent from '../../components/base/nav/TopNavComponent';
import LanguageSelector from '../../components/LanguageSelector';
import NavButtonComponent from '../../components/utils/nav/NavButtonComponent';
import NavItemComponent from '../../components/utils/nav/NavItemComponent';
import NavSectionComponent from '../../components/utils/nav/NavSectionComponent';
import PermissionDecoratorV2 from '../../components/utils/permission/PermissionDecoratorV2';
import { useCheckPermission } from '../../components/utils/permission/helpers/usePermission';
import StateDecorator from '../../components/utils/state/StateDecorator';
import { isDataState } from '../../store/data/state';
import { useAppSelector } from '../../store/hooks';
import { baseBackofficeNav } from '../sites/utils/SiteDecorator';
import HomeMapComponent from './utils/HomeMapComponent';

function HomeComponent(_props: object, ref: LegacyRef<HTMLDivElement>) {
  const { t } = useTranslation('menu');
  const backofficeNav = baseBackofficeNav.map((e) => ({
    ...e,
    name: t(e.name),
  }));
  const navigate = useNavigate();
  // TODO: require site_id_to_dashboard_id
  // TODO: link to first dashboard for site
  const projects = useAppSelector(
    (state) => state.data_slice.externals.projects
  );
  const sites = useAppSelector((state) => state.data_slice.externals.sites);

  const project_sites = useAppSelector(
    (state) => state.data_slice.externals.project_sites
  );

  const siteIdsToDashboardIds = useAppSelector(
    (state) => state.data_slice.externals.siteIdsToDashboardIds
  );

  const authentication = useAppSelector(
    (state) => state.data_slice.externals.authentication
  );

  const actions = useAppSelector((state) => state.data_slice.externals.actions);

  const {
    shouldHide: disableTopBarCollapse,
    canDisable: alsoDisableTopBarCollapse,
  } = useCheckPermission('top_bar_can_collapse');
  const shouldDisableCollapse =
    disableTopBarCollapse || alsoDisableTopBarCollapse;
  const { shouldHide: disableTopBarPin, canDisable: alsoDisableTopBarPin } =
    useCheckPermission('top_bar_can_be_pinned');
  const shouldDisablePin = disableTopBarPin || alsoDisableTopBarPin;
  const {
    shouldHide: disableCollapsedMenuButton,
    canDisable: alsoDisableCollapsedMenuButton,
  } = useCheckPermission('collapsed_menu_button_should_be_displayed');
  const shouldDisableCollapsedMenuButton =
    disableCollapsedMenuButton || alsoDisableCollapsedMenuButton;

  return StateDecorator(
    [
      projects,
      sites,
      project_sites,
      siteIdsToDashboardIds,
      authentication,
      actions,
    ],
    ref,
    () => {
      assert(isDataState(projects));
      assert(isDataState(sites));
      assert(isDataState(project_sites));
      assert(isDataState(siteIdsToDashboardIds));
      assert(isDataState(authentication));
      assert(isDataState(actions));

      return (
        <main className="home">
          <div>
            <TopNavBarComponent
              isPinnable={shouldDisablePin ? false : true}
              isCollapsible={shouldDisableCollapse ? false : true}
              barHeight={64}
              collapsedHeight={8}
              showCollapsedMenuButton={
                shouldDisableCollapsedMenuButton ? false : true
              }
              hideToggleShowLeftNav
              style={{ zIndex: '20' }}
              leftElement={
                <Link to="/" className="uk-link-reset">
                  <Stack
                    direction="row"
                    spacing={2}
                    sx={{ alignItems: 'center' }}
                  >
                    <span className="nav-icon">
                      <Box
                        component="img"
                        sx={{
                          maxHeight: 48,
                          width: 'auto',
                          objectFit: 'contain',
                        }}
                        alt="favicon"
                        src="/favicon.ico"
                      />
                    </span>
                    <div>
                      {t('HIGH IMPACT')}
                      <div className="uk-navbar-subtitle">
                        {t('GESTION ENVIRONNEMENTALE')}
                      </div>
                    </div>
                  </Stack>
                </Link>
              }
            >
              <NavSectionComponent>
                <Stack direction="row" spacing={2}>
                  <NavItemComponent className="nav-item-static">
                    <LanguageSelector big />
                  </NavItemComponent>
                  <PermissionDecoratorV2 item={'actions_button'}>
                    <NavItemComponent className="nav-item-static">
                      <div>
                        <div>
                          <button
                            className="uk-button-link"
                            type="button"
                            onClick={() => {
                              /** */
                            }}
                          >
                            {t('Actions')}
                          </button>
                          <div uk-dropdown="pos: bottom-center">
                            <ul className="uk-nav uk-dropdown-nav">
                              {Object.values(actions.data).map(
                                ({ label, url }, key) => (
                                  <li key={key} className="uk-active">
                                    <Link
                                      to={url}
                                      className="uk-link-reset"
                                      style={{ flexGrow: 1 }}
                                    >
                                      {label}
                                    </Link>
                                  </li>
                                )
                              )}
                            </ul>
                          </div>
                        </div>
                      </div>
                    </NavItemComponent>
                  </PermissionDecoratorV2>

                  <NavItemComponent className="nav-item-static">
                    <PermissionDecoratorV2 item={'configuration_button'}>
                      <div className="dropdown-permission-wrapper">
                        <button
                          className="uk-button-link"
                          type="button"
                          onClick={() => navigate(backofficeNav[0].url)}
                        >
                          {t('Configurations')}
                        </button>

                        <div uk-dropdown="pos: bottom-center">
                          <ul className="uk-nav uk-dropdown-nav">
                            {backofficeNav.map(
                              ({ name, url, itemName }, key) => (
                                <li key={key} className="uk-active">
                                  {itemName ? (
                                    <PermissionDecoratorV2 item={itemName}>
                                      <Link
                                        to={url}
                                        className="uk-link-reset"
                                        style={{ flexGrow: 1 }}
                                      >
                                        {name}
                                      </Link>
                                    </PermissionDecoratorV2>
                                  ) : (
                                    <Link
                                      to={url}
                                      className="uk-link-reset"
                                      style={{ flexGrow: 1 }}
                                    >
                                      {name}
                                    </Link>
                                  )}
                                </li>
                              )
                            )}
                          </ul>
                        </div>
                      </div>
                    </PermissionDecoratorV2>
                  </NavItemComponent>

                  <NavItemComponent className="nav-item-static">
                    <PermissionDecoratorV2 item={'help_button'}>
                      <div className="dropdown-permission-wrapper">
                        <button
                          className="uk-button-link"
                          type="button"
                          onClick={() =>
                            window.open(
                              'https://high-impact.fr/#contact',
                              '_blank',
                              'noopener,noreferrer'
                            )
                          }
                        >
                          {t('Aide')}
                        </button>

                        <div uk-dropdown="pos: bottom-center">
                          <ul className="uk-nav uk-dropdown-nav">
                            <li className="uk-active">
                              <Link
                                to="#"
                                className="uk-link-reset"
                                style={{ flexGrow: 1 }}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  window.open(
                                    'https://high-impact.fr/#contact',
                                    'noopener,noreferrer'
                                  );
                                }}
                              >
                                {t('Contact')}
                              </Link>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </PermissionDecoratorV2>
                  </NavItemComponent>

                  <NavItemComponent className="nav-item-stati">
                    <PermissionDecoratorV2 item={'user_profile_button'}>
                      <div className="dropdown-permission-wrapper">
                        <button className="uk-button-link" type="button">
                          {authentication.data.name}
                        </button>

                        <div uk-dropdown="pos: bottom-center">
                          <ul className="uk-nav uk-dropdown-nav">
                            <NavButtonComponent
                              icon="sign-out"
                              name={t('Se déconnecter')}
                              url="/logout"
                              mode="danger"
                            />
                          </ul>
                        </div>
                      </div>
                    </PermissionDecoratorV2>
                  </NavItemComponent>
                </Stack>
              </NavSectionComponent>
            </TopNavBarComponent>

            <div className="home-content" style={{ zIndex: '0' }}>
              <HomeMapComponent
                projects={Object.values(projects.data)}
                sites={sites}
                project_sites={project_sites}
                siteIdsToDashboardIds={siteIdsToDashboardIds.data}
              />
            </div>
          </div>
        </main>
      );
    }
  );
}

export default forwardRef(HomeComponent);
